import React from "react";
import { DIMENSIONS, orgDimensionAverage, dimensionAverage, scoreColor, SCALE_MAX } from "@/data/coachingData";
import { useI18n } from "@/i18n";
import { Brain, Activity, Target, Sparkles } from "lucide-react";

const ICONS = { mindset: Brain, behavior: Activity, outcome: Target };

export function DimensionSummary({ tiers, scores, insight = null, compact = false }) {
  const { t } = useI18n();
  const rows = DIMENSIONS.map((dim) => ({
    dim,
    avg: +orgDimensionAverage(scores, tiers, dim).toFixed(1),
  }));
  const lagging = rows.reduce((a, b) => (b.avg < a.avg ? b : a), rows[0]);

  const insightText = insight?.text?.trim();

  return (
    <div data-testid="dimension-summary">
      {insightText && (
        <div
          className="print-card flex items-start gap-2.5 rounded-xl border border-indigo-500/30 bg-indigo-500/5 p-3.5 mb-4"
          data-testid="dimension-insight"
        >
          <Sparkles size={15} className="text-indigo-600 mt-0.5 shrink-0" />
          <p className="text-sm text-slate-800 leading-relaxed print-text">
            {insight?.dimension && (
              <span className="text-[10px] uppercase tracking-widest font-bold text-indigo-700 mr-1.5">
                {t(`dim.${insight.dimension}`)}
              </span>
            )}
            {insightText}
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      {rows.map(({ dim, avg }) => {
        const Icon = ICONS[dim];
        const color = scoreColor(avg);
        const isLagging = dim === lagging.dim && rows.some((r) => r.avg !== lagging.avg);
        return (
          <div
            key={dim}
            className="print-card rounded-xl border bg-white p-4"
            style={{ borderColor: isLagging ? `${color}88` : "#1e293b" }}
            data-testid={`dimension-card-${dim}`}
          >
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-2">
                <Icon size={16} style={{ color }} />
                <span className="text-xs uppercase tracking-widest font-bold text-slate-700">
                  {t(`dim.${dim}`)}
                </span>
              </div>
              {isLagging && (
                <span
                  className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded"
                  style={{ background: `${color}22`, color }}
                >
                  {t("dim.lagging")}
                </span>
              )}
            </div>
            <p className="font-heading text-3xl font-extrabold tabular-nums print-text" style={{ color }}>
              {avg.toFixed(1)}
              <span className="text-sm font-semibold text-slate-500">/{SCALE_MAX}</span>
            </p>
            {!compact && <p className="text-[11px] text-slate-500 mt-1 leading-snug">{t(`dim.${dim}.desc`)}</p>}

            <div className="mt-3 space-y-1.5">
              {tiers.map((tier) => {
                const v = dimensionAverage(scores, tier.key, dim);
                return (
                  <div key={tier.key} className="flex items-center gap-2">
                    <span className="text-[10px] text-slate-500 w-20 shrink-0 truncate">
                      {t(`tierShort.${tier.key}`)}
                    </span>
                    <span className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <span
                        className="block h-full rounded-full"
                        style={{ width: `${(v / SCALE_MAX) * 100}%`, background: scoreColor(v) }}
                      />
                    </span>
                    <span className="text-[10px] font-mono text-slate-600 tabular-nums w-6 text-right">
                      {v.toFixed(1)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
      </div>
    </div>
  );
}
